import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Invoice {
  id: string
  customer_id: string
  customer_name: string
  amount: number
  currency: string
  status: string
  due_date: string
  paid_at: string | null
  items: { description: string; amount: number }[]
}

export default function BillingPage() {
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [transactions, setTransactions] = useState<any[]>([])
  const [statistics, setStatistics] = useState<any>(null)
  const [filterStatus, setFilterStatus] = useState<string | null>(null)

  useEffect(() => {
    fetchData()
  }, [filterStatus])

  const fetchData = async () => {
    const statusParam = filterStatus ? `?status=${filterStatus}` : ''
    const [invRes, txnRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/billing/invoices' + statusParam),
      fetch(API_BASE + '/billing/transactions'),
      fetch(API_BASE + '/billing/statistics')
    ])
    if (invRes.ok) setInvoices((await invRes.json()).invoices || [])
    if (txnRes.ok) setTransactions((await txnRes.json()).transactions || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800'
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'overdue': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-EU', { style: 'currency', currency }).format(amount)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Invoice & Billing</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Create Invoice</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Revenue (Month)</div>
              <div className="text-2xl font-bold text-green-600">{formatCurrency(statistics.total_revenue_month, 'EUR')}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Pending</div>
              <div className="text-2xl font-bold text-yellow-600">{formatCurrency(statistics.pending_amount, 'EUR')}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Overdue</div>
              <div className="text-2xl font-bold text-red-600">{formatCurrency(statistics.overdue_amount, 'EUR')}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Paid Invoices</div>
              <div className="text-2xl font-bold">{statistics.paid_invoices}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Pending Invoices</div>
              <div className="text-2xl font-bold">{statistics.pending_invoices}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Collection Rate</div>
              <div className="text-2xl font-bold text-green-600">{statistics.collection_rate}%</div>
            </div>
          </div>
        )}

        <div className="flex gap-4 mb-6">
          <button onClick={() => setFilterStatus(null)} className={`px-4 py-2 rounded-lg ${!filterStatus ? 'bg-blue-600 text-white' : 'bg-white border'}`}>All</button>
          <button onClick={() => setFilterStatus('paid')} className={`px-4 py-2 rounded-lg ${filterStatus === 'paid' ? 'bg-blue-600 text-white' : 'bg-white border'}`}>Paid</button>
          <button onClick={() => setFilterStatus('pending')} className={`px-4 py-2 rounded-lg ${filterStatus === 'pending' ? 'bg-blue-600 text-white' : 'bg-white border'}`}>Pending</button>
          <button onClick={() => setFilterStatus('overdue')} className={`px-4 py-2 rounded-lg ${filterStatus === 'overdue' ? 'bg-blue-600 text-white' : 'bg-white border'}`}>Overdue</button>
        </div>

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Invoices</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {invoices.map(inv => (
                  <tr key={inv.id}>
                    <td className="px-6 py-4 font-mono text-sm">{inv.id}</td>
                    <td className="px-6 py-4">{inv.customer_name}</td>
                    <td className="px-6 py-4 font-semibold">{formatCurrency(inv.amount, inv.currency)}</td>
                    <td className="px-6 py-4 text-sm">{new Date(inv.due_date).toLocaleDateString()}</td>
                    <td className="px-6 py-4"><span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(inv.status)}`}>{inv.status}</span></td>
                    <td className="px-6 py-4">
                      <button className="px-3 py-1 text-xs bg-blue-100 text-blue-800 rounded hover:bg-blue-200">View</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Recent Transactions</h3>
            <div className="space-y-3">
              {transactions.slice(0, 5).map(txn => (
                <div key={txn.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-center">
                    <span className="font-mono text-sm">{txn.invoice_id}</span>
                    <span className={`px-2 py-1 rounded text-xs ${txn.status === 'succeeded' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{txn.status}</span>
                  </div>
                  <div className="flex justify-between mt-1 text-sm">
                    <span className="text-gray-500">{new Date(txn.created_at).toLocaleString()}</span>
                    <span className="font-semibold">{formatCurrency(txn.amount, 'EUR')}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
